import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { StatusPill } from "@/components/StatusPill";
import { api } from "@/lib/api";
import { FileStack, Users, Euro, Coins, Zap, Clock, Star, Gauge, Search, ShieldAlert, AlertTriangle, ArrowRight } from "lucide-react";
import { ChecksumCoverage } from "@/components/ChecksumCoverage";

const STATUS_COLORS = { received: "#A1A1AA", in_progress: "#FFB020", completed: "#00E5A0", rejected: "#E4002B" };
const STATUS_LABELS = { received: "Reçu", in_progress: "En cours", completed: "Terminé", rejected: "Refusé" };
const SVC_COLOR = "#00E5FF";

export const AdminDashboard = () => {
  const [a, setA] = useState(null);
  const [flagged, setFlagged] = useState([]);

  useEffect(() => { api.get("/admin/analytics").then(({ data }) => setA(data)); }, []);
  useEffect(() => { api.get("/admin/flagged").then(({ data }) => setFlagged(data.items || [])).catch(() => {}); }, []);

  if (!a) return <AppShell mode="admin"><div className="font-mono text-xs text-zinc-500">Chargement des statistiques...</div></AppShell>;

  const t = a.totals;
  const maxRev = Math.max(...a.revenue_by_month.map((m) => m.revenue), 1);
  const totalOrders = a.totals.orders || 1;
  const maxSvc = Math.max(...a.top_services.map((s) => s.count), 1);
  const maxBrand = Math.max(...a.top_brands.map((s) => s.count), 1);
  const searched = a.searched_vehicles || [];
  const maxSearched = Math.max(...searched.map((s) => s.count), 1);

  const kpis = [
    { icon: Euro, label: "Revenus", value: `${t.revenue}€`, color: "#00E5A0" },
    { icon: FileStack, label: "Fichiers", value: t.orders, color: "#00E5FF" },
    { icon: Zap, label: "Taux auto", value: `${t.auto_rate}%`, color: "#8B5CF6" },
    { icon: Clock, label: "Délai moyen", value: `${t.avg_processing_hours}h`, color: "#FFB020" },
    { icon: Star, label: "Note moyenne", value: t.avg_rating || "—", color: "#FFB020" },
    { icon: Users, label: "Clients", value: t.users, color: "#00E5FF" },
  ];

  return (
    <AppShell mode="admin">
      <div className="mb-8">
        <div className="font-mono text-xs text-aurora-purple tracking-[0.3em] uppercase mb-2">// Pilotage atelier</div>
        <h1 className="font-display text-4xl font-bold uppercase tracking-tight">Centre de Commandement</h1>
      </div>

      <div className="mb-6"><ChecksumCoverage /></div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="glass rounded-md p-4" data-testid={`kpi-${k.label}`}>
              <Icon className="w-5 h-5 mb-2" style={{ color: k.color }} />
              <div className="font-display text-2xl font-bold" style={{ color: k.color }}>{k.value}</div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mt-1">{k.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        {/* Revenue chart */}
        <div className="glass rounded-md p-6 lg:col-span-2">
          <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-1">Revenus · 6 mois</h3>
          <div className="font-mono text-[10px] text-zinc-500 mb-5">Crédits vendus : {t.credits_sold} · consommés : {t.credits_spent}</div>
          <div className="flex items-end gap-3 h-44">
            {a.revenue_by_month.map((m) => (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-2">
                <div className="font-mono text-[10px] text-aurora-cyan">{m.revenue > 0 ? `${m.revenue}€` : ""}</div>
                <div className="w-full bg-gradient-to-t from-aurora-cyan/80 to-aurora-purple/60 rounded-t-sm transition-all" style={{ height: `${Math.max(4, (m.revenue / maxRev) * 140)}px` }} />
                <div className="font-mono text-[9px] text-zinc-600">{m.month.slice(5)}/{m.month.slice(2, 4)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Status breakdown */}
        <div className="glass rounded-md p-6">
          <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-5">Répartition statuts</h3>
          <div className="space-y-4">
            {Object.keys(STATUS_LABELS).map((s) => {
              const count = a.by_status[s] || 0;
              return (
                <div key={s}>
                  <div className="flex justify-between font-mono text-[10px] uppercase tracking-widest mb-1">
                    <span style={{ color: STATUS_COLORS[s] }}>{STATUS_LABELS[s]}</span>
                    <span className="text-zinc-400">{count}</span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(count / totalOrders) * 100}%`, background: STATUS_COLORS[s] }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Top services */}
        <div className="glass rounded-md p-6">
          <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-5">Solutions populaires</h3>
          <div className="space-y-3">
            {a.top_services.length === 0 ? <p className="text-zinc-500 text-sm">—</p> : a.top_services.map((s) => (
              <div key={s.key}>
                <div className="flex justify-between font-mono text-[11px] mb-1"><span className="text-zinc-300 uppercase">{s.key}</span><span className="text-aurora-cyan">{s.count}</span></div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(s.count / maxSvc) * 100}%`, background: SVC_COLOR }} /></div>
              </div>
            ))}
          </div>
        </div>

        {/* Top brands */}
        <div className="glass rounded-md p-6">
          <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-5">Top marques</h3>
          <div className="space-y-3">
            {a.top_brands.length === 0 ? <p className="text-zinc-500 text-sm">—</p> : a.top_brands.map((s) => (
              <div key={s.key}>
                <div className="flex justify-between font-mono text-[11px] mb-1"><span className="text-zinc-300">{s.key}</span><span className="text-aurora-purple">{s.count}</span></div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(s.count / maxBrand) * 100}%`, background: "#8B5CF6" }} /></div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent */}
        <div className="glass rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold uppercase tracking-tight">Derniers fichiers</h3>
            <Link to="/admin/orders" className="font-mono text-[10px] text-aurora-cyan uppercase tracking-widest hover:underline">Tout</Link>
          </div>
          <div className="space-y-2">
            {a.recent.length === 0 ? <p className="text-zinc-500 text-sm">Aucun.</p> : a.recent.map((o) => (
              <Link key={o.id} to={`/admin/orders/${o.id}`} className="flex items-center justify-between py-2 hover:bg-white/5 rounded-sm px-2 transition-colors">
                <span className="font-display uppercase tracking-tight text-sm truncate">{o.vehicle_brand} {o.vehicle_model}</span>
                <StatusPill status={o.status} />
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* QC — files to verify */}
      <div className="glass rounded-md p-6 mt-6" data-testid="qc-flagged-panel">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-lg font-semibold uppercase tracking-tight flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-yellow-400" /> Contrôle qualité — à vérifier
          </h3>
          <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">{flagged.length} fichier{flagged.length > 1 ? "s" : ""}</span>
        </div>
        <p className="font-mono text-[10px] text-zinc-500 mb-5">Fichiers signalés "déjà modifié" par le scanner + détections ECU contestées par les clients.</p>
        {flagged.length === 0 ? (
          <p className="text-zinc-500 text-sm">Aucun fichier à vérifier. 👌</p>
        ) : (
          <div className="space-y-2">
            {flagged.map((f) => (
              <Link key={f.id} to={`/admin/orders/${f.id}`} data-testid={`qc-row-${f.id}`}
                className="group flex items-center gap-4 p-3 rounded-sm border border-white/10 hover:border-yellow-400/40 hover:bg-white/5 transition-colors">
                <div className="w-9 h-9 grid place-items-center rounded-sm bg-yellow-400/10 border border-yellow-400/30 shrink-0">
                  {f.ecu_report ? <AlertTriangle className="w-4 h-4 text-yellow-400" /> : <ShieldAlert className="w-4 h-4 text-yellow-400" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display uppercase tracking-tight truncate">{f.vehicle || "Véhicule"}</div>
                  <div className="font-mono text-[10px] text-zinc-500 truncate">
                    {f.ecu_detected || "ECU ?"}
                    {f.prior_tuning && <span className="text-yellow-400"> · {f.tuner_tags.join(", ") || "outil détecté"}</span>}
                    {f.ecu_report && <span className="text-aurora-cyan"> · signalé : {f.ecu_report.correct_ecu || "ECU à corriger"}</span>}
                  </div>
                </div>
                {typeof f.score === "number" && (
                  <span className={`font-mono text-xs font-bold shrink-0 ${f.score >= 80 ? "text-signal-success" : "text-yellow-400"}`}>{f.score}/100</span>
                )}
                <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-yellow-400 group-hover:translate-x-1 transition-all shrink-0" />
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Most-searched vehicles (public estimator leads) */}
      <div className="glass rounded-md p-6 mt-6" data-testid="searched-vehicles-panel">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-lg font-semibold uppercase tracking-tight flex items-center gap-2">
            <Search className="w-4 h-4 text-aurora-cyan" /> Véhicules les plus recherchés
          </h3>
          <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">Estimateur public · leads</span>
        </div>
        <p className="font-mono text-[10px] text-zinc-500 mb-5">Ce que vos visiteurs estiment — priorisez vos imports de solutions sur ces modèles.</p>
        {searched.length === 0 ? (
          <p className="text-zinc-500 text-sm">Aucune recherche pour le moment.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {searched.map((s) => (
              <div key={s.key}>
                <div className="flex justify-between font-mono text-[11px] mb-1">
                  <span className="text-zinc-300 truncate uppercase">{s.key}</span>
                  <span className="text-aurora-cyan">{s.count}</span>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(s.count / maxSearched) * 100}%`, background: "linear-gradient(90deg,#0064B0,#C8102E)" }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </AppShell>
  );
};
